/**
 * Every endpoint this panel calls, named and typed, in one file.
 *
 * Screens import these rather than writing paths, so the URL of a thing exists
 * once.
 *
 * THE ROW SHAPES ARE NOT DECLARED HERE ANY MORE. This header used to argue that
 * they should not go in `@ewn/shared` because that "would make the storefront's
 * bundle carry the operator's vocabulary". An interface is erased by the
 * compiler, so the cost being avoided was zero -- and the two hand-written
 * copies had meanwhile disagreed about a nullable column, dropped a field the
 * worker computes on purpose, and given up on three shapes entirely with
 * `[key: string]: unknown`.
 *
 * WHAT STAYS HERE IS THE PATHS, and nothing else. An earlier version of this
 * paragraph kept the content APIs' `camelCase` answers as well, on the grounds
 * that "no other package reads" them -- which was false for all nine of them:
 * the worker hand-declared every one, and two had drifted as far as different
 * NAMES for the same wire shape (`AssetMeta`/`AdminAsset`,
 * `AdminRow`/`AdminAccount`), so the divergence this was meant to prevent could
 * not have been found by grepping a name.
 */
import { apiDelete, apiGet, apiPost, apiPut, apiUpload } from "./client";
import type {
  AdminAccount,
  AdminArticle,
  AdminAsset,
  AdminAuditRow,
  AdminBadges,
  AdminCustomerDetail,
  AdminCustomerRow,
  AdminEsimDetail,
  AdminEsimRow,
  AdminLocale,
  AdminCoupon,
  AdminLogClear,
  CouponWrite,
  AdminReport,
  ReportRange,
  AdminLogPage,
  LogFilter,
  AdminOrderDetail,
  AdminOrderRow,
  AdminPackageRow,
  AdminSupplierCostRow,
  AdminPaymentRow,
  AdminBlockRow,
  AdminFloorRow,
  AdminArticleLocale,
  AdminSeoView,
  AdminSettingRow,
  SiteRebuildDetail,
  AdminStats,
  OrderFilter,
  PaymentFilter,
  SearchQuery,
  TestMailTemplate,
  AuthState,
  Namespace,
  Page,
  PackageWrite,
  PageKey,
  PublishRow,
  RobotsValue,
} from "@ewn/shared";

/**
 * THE SHAPES, passed through from `@ewn/shared` -- the whole package barrel,
 * not a chosen subset, so this re-exports storefront vocabulary too.
 *
 * `export type *`, with no list: a hand-written one is a second inventory to
 * keep level with the first, and this was one -- it had already missed
 * `AdminOrderEsimRow`, so `OrderScreen` imported two of its three row types
 * from here and the third from `@ewn/shared` in the same file, and it carried
 * `ListQuery` while omitting the three filters beside it.
 *
 * TYPES ONLY, and that is not a limitation worth hiding: `isPageKey`,
 * `isRobotsValue`, `isNamespace` and `ALWAYS_NOINDEX` are runtime values and
 * the screens that validate import them from `@ewn/shared` directly. This is a
 * convenience for the screens that only need shapes, not a claim to be the one
 * place anything comes from.
 */
export type * from "@ewn/shared";

/** Every write answers `{ ok: true }` plus whatever that write had to say. */
export interface WriteOk extends SiteRebuildDetail {
  ok: true;
  warnings?: string[];
  [key: string]: unknown;
}

// ------------------------------------------------------------------- session

export interface Me {
  adminId: string;
  email: string;
  name: string | null;
  ip: string;
}

export const getAuthState = () => apiGet<AuthState>("/auth/state");
export const getMe = () => apiGet<Me>("/admin/me");

/** Signing in takes NO email: the account is whoever Cloudflare Access says. */
export const login = (password: string) =>
  apiPost<{ ok: true; token: string; email: string; name: string | null }>("/auth/login", {
    password,
  });

/** First run, an invitation, or an account whose password was revoked. */
export const claim = (password: string) =>
  apiPost<{ ok: true; token: string }>("/auth/claim", { password });

export const logout = () => apiPost<{ ok: true }>("/auth/logout");

export const changePassword = (current: string, next: string) =>
  apiPost<{ ok: true; token: string }>("/auth/password", { current, next });

// -------------------------------------------------------------------- badges

/** The menu's counts for the signed-in administrator. */
export const getBadges = () => apiGet<AdminBadges>("/admin/badges");

/** This administrator has looked at Orders; answers the counts as of then. */
export const markOrdersSeen = () => apiPost<AdminBadges>("/admin/orders/seen");

// -------------------------------------------------------------------- orders

/**
 * A filter as a query string: its fields, set and not empty. Every field is a
 * string by type, so a caller cannot hand this a number to lose. The `typeof`
 * is for `Object.entries`, which forgets that and types each value loosely.
 */
const query = <T extends { [K in keyof T]?: string }>(params: T): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "string" && value !== "") search.set(key, value);
  }
  const text = search.toString();
  return text ? `?${text}` : "";
};

export const listOrders = (q: OrderFilter = {}) =>
  apiGet<Page<AdminOrderRow>>(`/admin/orders${query(q)}`);

export const readOrder = (code: string) =>
  apiGet<AdminOrderDetail>(`/admin/orders/${encodeURIComponent(code)}`);

export const approveOrder = (code: string) =>
  apiPost<WriteOk>(`/admin/orders/${encodeURIComponent(code)}/approve`);

export const closeOrder = (code: string) =>
  apiPost<WriteOk>(`/admin/orders/${encodeURIComponent(code)}/close`);

export const retryOrder = (code: string) =>
  apiPost<WriteOk>(`/admin/orders/${encodeURIComponent(code)}/retry`);

export const noteRefund = (code: string, note: string) =>
  apiPost<WriteOk>(`/admin/orders/${encodeURIComponent(code)}/refund-note`, { note });

/** A new link for the order; every earlier one stops working. */
export const reissueOrderLink = (code: string) =>
  apiPost<WriteOk & { link: string }>(`/admin/orders/${encodeURIComponent(code)}/link`);

// --------------------------------------------------------------------- esims

/** `q`: an address, number, ICCID or order code; the worker reads which. */
export const listEsims = (q: SearchQuery = {}) =>
  apiGet<Page<AdminEsimRow>>(`/admin/esims${query(q)}`);

export const readEsim = (iccid: string) =>
  apiGet<AdminEsimDetail>(`/admin/esims/${encodeURIComponent(iccid)}`);

/** Asks the supplier about this line now; refused soon after the last ask. */
export const refreshEsim = (iccid: string) =>
  apiPost<WriteOk & { profile: boolean; usage: boolean }>(
    `/admin/esims/${encodeURIComponent(iccid)}/refresh`,
  );

// ------------------------------------------------------------------ payments

export const listPayments = (q: PaymentFilter = {}) =>
  apiGet<Page<AdminPaymentRow>>(`/admin/payments${query(q)}`);

/**
 * "I have looked at this." The id is the PROVIDER'S payment id, which is what
 * the list sends -- not an order code.
 *
 * This does not un-park the payment: the row stays out of the sweeper's reach
 * and leaves the operator's queue. `worker/src/domain/admin-payments.ts` says
 * why those are two different facts.
 */
export const dismissPaymentReview = (paymentId: string, note: string) =>
  apiPost<WriteOk>(`/admin/payments/${encodeURIComponent(paymentId)}/dismiss`, { note });

/** Every coin, offered or not, with its minimums: yours, learned, applied. */
export const listFloors = () => apiGet<{ rows: AdminFloorRow[] }>("/admin/floors");

/** Switches one coin on or off at checkout, and rebuilds the site's FAQ. */
export const setCoinEnabled = (psysCid: string, enabled: boolean) =>
  apiPut<WriteOk>(`/admin/floors/${encodeURIComponent(psysCid)}/enabled`, { enabled });

/** A coin's minimum, set or cleared: checkout at once, catalogue within 60s. */
export const setOperatorFloor = (psysCid: string, minCents: number | null) =>
  apiPut<WriteOk>(`/admin/floors/${encodeURIComponent(psysCid)}`, { minCents });

/** Networks the order allowance has blocked. */
export const listBlocks = () => apiGet<{ rows: AdminBlockRow[] }>("/admin/blocks");

/** Lifts a network's block now; its order count starts again from here. */
export const liftBlock = (net: string) => apiPost<WriteOk>("/admin/blocks/lift", { net });

/** An overpayment's surplus recorded as sent back. Moves no money. */
export const markSurplusRefunded = (paymentId: string, note: string) =>
  apiPost<WriteOk>(`/admin/payments/${encodeURIComponent(paymentId)}/surplus-refunded`, { note });

/** A parked payment back to the automatic checks. */
export const recheckPayment = (paymentId: string) =>
  apiPost<WriteOk>(`/admin/payments/${encodeURIComponent(paymentId)}/recheck`);

// --------------------------------------------------------------- audit + logs

export const listAudit = (q: { limit?: string; cursor?: string } = {}) =>
  apiGet<Page<AdminAuditRow>>(`/admin/audit${query(q)}`);

export const listCoupons = () => apiGet<{ rows: AdminCoupon[] }>("/admin/coupons");

/** One named coupon or `count` generated ones; answers the codes it made. */
export const createCoupons = (write: CouponWrite, allowBelowCost = false) =>
  apiPost<WriteOk & { codes: string[] }>("/admin/coupons", {
    ...write,
    ...(allowBelowCost ? { allowBelowCost } : {}),
  });

export const setCouponEnabled = (code: string, enabled: boolean, allowBelowCost = false) =>
  apiPut<WriteOk>(`/admin/coupons/${encodeURIComponent(code)}/enabled`, {
    enabled,
    ...(allowBelowCost ? { allowBelowCost } : {}),
  });

/** Sales over a range of Istanbul days, both counted. */
export const readReport = (range: ReportRange) =>
  apiGet<AdminReport>(`/admin/report${query({ from: range.from, to: range.to })}`);

export const listLogs = (q: LogFilter = {}) => apiGet<AdminLogPage>(`/admin/logs${query(q)}`);

/** Deletes the log up to the newest line the operator saw. */
export const clearLogs = (throughId: number) =>
  apiPost<WriteOk & AdminLogClear>("/admin/logs/clear", { throughId });

// ------------------------------------------------------------------ settings

export const listSettings = () => apiGet<{ settings: AdminSettingRow[] }>("/admin/settings");

export const putSetting = (key: string, value: boolean | number | string, allowBelowCost = false) =>
  apiPut<WriteOk>(`/admin/settings/${encodeURIComponent(key)}`, {
    value,
    ...(allowBelowCost ? { allowBelowCost } : {}),
  });

/** Sample values; `delivered` says whether it reached an inbox. */
export const sendTestMail = (template: TestMailTemplate, to: string) =>
  apiPost<WriteOk & { delivered: boolean }>("/admin/email/test", { template, to });

// --------------------------------------------------------------------- stats

export const getStats = () => apiGet<AdminStats>("/admin/stats");

// ---------------------------------------------------------------------- i18n

export const listLocales = () => apiGet<{ locales: AdminLocale[] }>("/admin/i18n/locales");

export const saveLocale = (code: string, body: { name: string; flag: string | null; sort: number }) =>
  apiPut<WriteOk>(`/admin/i18n/locales/${encodeURIComponent(code)}`, body);

export const setLocaleEnabled = (code: string, enabled: boolean) =>
  apiPut<WriteOk>(`/admin/i18n/locales/${encodeURIComponent(code)}/enabled`, { enabled });

export const readTranslations = (locale: string, namespace: Namespace) =>
  apiGet<{ locale: string; namespace: string; strings: Record<string, string> }>(
    `/admin/i18n/${encodeURIComponent(locale)}/${namespace}`,
  );

export const saveTranslations = (
  locale: string,
  namespace: Namespace,
  entries: Record<string, string>,
) => apiPut<WriteOk>(`/admin/i18n/${encodeURIComponent(locale)}/${namespace}`, { entries });

export const deleteTranslation = (locale: string, namespace: Namespace, key: string) =>
  apiDelete<WriteOk>(
    `/admin/i18n/${encodeURIComponent(locale)}/${namespace}/${encodeURIComponent(key)}`,
  );

// ----------------------------------------------------------------------- seo

export const readSeo = (locale: string) =>
  apiGet<AdminSeoView>(`/admin/seo${query({ locale })}`);

export interface SeoPageBody {
  slug: string;
  title: string;
  metaDescription: string;
  robots: RobotsValue;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImageKey: string | null;
  h1: string;
}

export const saveSeoPage = (pageKey: PageKey, locale: string, body: SeoPageBody) =>
  apiPut<WriteOk & { redirectAdded: string | null }>(
    `/admin/seo/pages/${encodeURIComponent(pageKey)}/${encodeURIComponent(locale)}`,
    body,
  );

// ------------------------------------------------------------------- content

export const listArticles = () => apiGet<{ articles: AdminArticle[] }>("/admin/content/articles");

export const readArticle = (id: string, locale: string) =>
  apiGet<AdminArticleLocale & { bodyMd: string }>(
    `/admin/content/articles/${encodeURIComponent(id)}/${encodeURIComponent(locale)}`,
  );

export const createArticle = () =>
  apiPost<WriteOk & { id: string }>("/admin/content/articles");

export const setArticleCover = (id: string, coverAssetKey: string | null) =>
  apiPut<WriteOk>(`/admin/content/articles/${encodeURIComponent(id)}`, { coverAssetKey });

export const saveArticleLocale = (
  id: string,
  locale: string,
  body: { slug: string; title: string; excerpt: string | null; bodyMd: string },
) =>
  apiPut<WriteOk & { redirectAdded: string | null }>(
    `/admin/content/articles/${encodeURIComponent(id)}/${encodeURIComponent(locale)}`,
    body,
  );

export const publishArticle = (id: string) =>
  apiPost<WriteOk>(`/admin/content/articles/${encodeURIComponent(id)}/publish`);

export const unpublishArticle = (id: string) =>
  apiPost<WriteOk>(`/admin/content/articles/${encodeURIComponent(id)}/unpublish`);

export const deleteArticle = (id: string) =>
  apiDelete<WriteOk>(`/admin/content/articles/${encodeURIComponent(id)}`);

// -------------------------------------------------------------------- assets

export const listAssets = () => apiGet<{ assets: AdminAsset[] }>("/admin/assets");

export const uploadAsset = (form: FormData) =>
  apiUpload<WriteOk & { key: string; replaced: boolean }>("/admin/assets", form);

export const setAssetAlt = (key: string, alt: Record<string, string>) =>
  apiPut<WriteOk>(`/admin/assets/${encodeURIComponent(key)}/alt`, { alt });

export const deleteAsset = (key: string) =>
  apiDelete<WriteOk>(`/admin/assets/${encodeURIComponent(key)}`);

// ------------------------------------------------------------------- publish

export const listPublishes = () => apiGet<{ publishes: PublishRow[] }>("/admin/publish");

export const publish = (kind: "site" | "staging") =>
  apiPost<WriteOk & { publishId: string }>("/admin/publish", { kind });

// -------------------------------------------------------------------- admins

export const listAdmins = () => apiGet<{ admins: AdminAccount[] }>("/admin/admins");

export const inviteAdmin = (email: string, name: string) =>
  apiPost<WriteOk & { id: string; email: string }>("/admin/admins", { email, name });

export const setAdminStatus = (id: string, status: "active" | "suspended") =>
  apiPut<WriteOk>(`/admin/admins/${encodeURIComponent(id)}/status`, { status });

export const revokeAdminPassword = (id: string) =>
  apiPost<WriteOk>(`/admin/admins/${encodeURIComponent(id)}/revoke-password`);

// ------------------------------------------------------------------ packages

export const listPackages = () => apiGet<Page<AdminPackageRow>>("/admin/packages");

/**
 * A save may come back 409 `needs_confirmation` -- a price below cost -- and
 * goes through when repeated with `allowBelowCost`. The flag is sent only when
 * set, so the ordinary save carries nothing the worker has to ignore.
 *
 * A successful save also starts a site build; the answer says `published`.
 */
export const createPackage = (id: string, body: PackageWrite, allowBelowCost = false) =>
  apiPost<WriteOk>("/admin/packages", { id, ...body, ...(allowBelowCost ? { allowBelowCost } : {}) });

export const updatePackage = (id: string, body: PackageWrite, allowBelowCost = false) =>
  apiPut<WriteOk>(`/admin/packages/${encodeURIComponent(id)}`, {
    ...body,
    ...(allowBelowCost ? { allowBelowCost } : {}),
  });

/**
 * One discount for a list of packages. Asked about like a save when it sells
 * one below cost, and rebuilds the site like one.
 */
export const setPackageDiscounts = (ids: string[], pct: number, allowBelowCost = false) =>
  apiPost<WriteOk>("/admin/packages/discounts", {
    ids,
    pct,
    ...(allowBelowCost ? { allowBelowCost } : {}),
  });

/**
 * Usually refuses, and the refusal is the point.
 *
 * `orders.package_id` references this row and D1 does not enforce it, so a
 * delete of a package anybody has bought would SUCCEED and orphan those
 * orders. The worker counts first; the message it sends back names the count.
 */
export const deletePackage = (id: string) =>
  apiDelete<WriteOk>(`/admin/packages/${encodeURIComponent(id)}`);

/** Every package's stored cost beside eSIM Go's catalogue price for it now. */
export const readSupplierCosts = () =>
  apiGet<{ rows: AdminSupplierCostRow[] }>("/admin/packages/supplier-costs");

/**
 * Writes eSIM Go's price as the cost of the packages named. Ids only: the
 * worker asks the supplier again and writes what it states. Starts no build.
 */
export const applySupplierCosts = (ids: string[]) =>
  apiPost<WriteOk & { changed: string[]; unknown: string[] }>(
    "/admin/packages/supplier-costs",
    { ids },
  );

// ----------------------------------------------------------------- customers

export const listCustomers = (q: SearchQuery = {}) =>
  apiGet<Page<AdminCustomerRow>>(`/admin/customers${query(q)}`);

/**
 * One customer, by their address.
 *
 * Encoded once here and decoded once in the worker: an address contains `@`
 * and may contain `+`, and `+` in an unencoded path segment is a space.
 */
export const readCustomer = (email: string) =>
  apiGet<AdminCustomerDetail>(`/admin/customers/${encodeURIComponent(email)}`);
